import { supabase } from './supabase';
import type { TechnicalDetail } from './errorMessage';

/* =============================================================================
 * Getting the real fault to the one person who can fix it
 * -----------------------------------------------------------------------------
 * The company is shown a sentence and a reference. Everything else — the
 * message, the SQLSTATE, the hint, the detail, the stack, what was being
 * attempted, which screen, which build, and the instant it happened — goes to
 * the platform operator, where it is the difference between a fix and a
 * photograph of somebody's phone arriving days later.
 *
 * -----------------------------------------------------------------------------
 * WHY IT NEVER THROWS AND NEVER WAITS
 * -----------------------------------------------------------------------------
 * This runs while somebody is already looking at a failure. A reporter that
 * rejects would produce a second one on top of it, and an awaited one would
 * hold the message off the screen behind a network call that may be the very
 * thing that is broken. So the caller gets a reference immediately and the
 * report goes out behind it, and every failure of the reporter is swallowed.
 *
 * The reference is generated here rather than returned by the server for the
 * same reason: the person needs to be able to quote it whether or not the
 * report ever arrives, and a reference on a screen that matches nothing in the
 * table is itself a useful fact — it says the browser could not reach us.
 * ========================================================================== */

/**
 * Short, unambiguous, and readable down a phone line.
 *
 * No I, O, 0 or 1 — this gets read aloud to somebody in an office by a person
 * standing on a site, and those four are where that goes wrong. Two groups of
 * four, because an unbroken run of eight is misread and re-read.
 */
const ALPHABET = 'ACDEFGHJKLMNPQRTUVWXY2345789';

export function newReference(): string {
  const bytes = new Uint8Array(8);

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    /* Server-side rendering, an old WebView, a test. A weaker reference is
       still a reference; refusing to make one would lose the report. */
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }

  const chars = [...bytes].map((b) => ALPHABET[b % ALPHABET.length]);
  return `${chars.slice(0, 4).join('')}-${chars.slice(4).join('')}`;
}

/* -----------------------------------------------------------------------------
 * The same fault, again
 * -----------------------------------------------------------------------------
 * A failing query under React Query retries, and a person who does not know
 * what else to do presses the button again. Without this, one broken function
 * becomes forty rows that are all the same row, and the operator's screen shows
 * the loudest fault rather than the most important one.
 *
 * Keyed on the message and code, so the *same* fault reuses its reference —
 * which is also what somebody expects when they press it twice and read out the
 * number on the screen.
 * -------------------------------------------------------------------------- */
const WINDOW_MS = 5 * 60 * 1000;
const seen = new Map<string, { reference: string; at: number }>();

/** Kept small: this is a phone that may be open for days. */
function forget(now: number): void {
  if (seen.size < 50) return;
  for (const [key, entry] of seen) {
    if (now - entry.at > WINDOW_MS) seen.delete(key);
  }
}

function platformName(): string {
  /* A development build says so.
     -------------------------------------------------------------------------
     Three reports arrived on the operator's screen reading "0.1.0 · web" and
     looking exactly like faults from a customer. All three were the dev server:
     two chunks that stopped existing because a file was saved mid-request, and
     a context that lost its identity when Vite hot-replaced the module holding
     it. None of them can happen in a built bundle — there is one module graph
     and nothing replaces it underneath a mounted tree.

     They are still worth filing; somebody testing wants to see their own
     mistakes arrive. What is not acceptable is that they are indistinguishable
     from a company's, because an operator triaging a queue reads the build
     column first and a dev fault answered under a real one wastes the hour
     that a real one deserved. */
  const build = import.meta.env.DEV ? ' (dev)' : '';

  if (typeof navigator === 'undefined') return `unknown${build}`;
  /* Capacitor sets this on the window. Which one it is decides whether an
     operator is looking for a WebView problem or a browser one. */
  const capacitor = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  if (capacitor?.getPlatform) return `${capacitor.getPlatform()}${build}`;
  return `web${build}`;
}

/**
 * File a technical failure, and hand back the reference to show.
 *
 * Returns immediately. The insert happens behind it and cannot throw.
 */
export function reportTechnicalFailure(
  detail: TechnicalDetail,
  kind: string,
  doing?: string,
): string {
  const now = Date.now();
  const key = `${detail.code ?? ''}|${detail.message}`;

  const already = seen.get(key);
  if (already && now - already.at < WINDOW_MS) {
    /* Same fault, same reference, and nothing sent. The operator already has
       one of these; a hundred more say only that somebody kept trying. */
    return already.reference;
  }

  const reference = newReference();
  seen.set(key, { reference, at: now });
  forget(now);

  /* A permission refusal and a dropped connection are facts about a session,
     not about the software. Reported all the same — a permission wall somebody
     keeps hitting is usually a role set up wrongly, and that is the operator's
     to fix — but never awaited and never allowed to matter. */
  void send(reference, detail, kind, doing);

  return reference;
}

async function send(
  reference: string,
  detail: TechnicalDetail,
  kind: string,
  doing?: string,
): Promise<void> {
  try {
    await supabase.rpc('report_error', {
      p_reference: reference,
      p_occurred_at: new Date().toISOString(),
      p_message: detail.message,
      p_code: detail.code,
      p_detail: detail.detail,
      p_hint: detail.hint,
      p_stack: detail.stack,
      /* What was being attempted and how it failed, in that order — the two
         things an operator reads before anything else. */
      p_doing: doing ? `${doing} · ${kind}` : kind,
      /* Read from the address bar rather than pushed here by the router.
         There was a noteRoute() for the router to call and nothing called it,
         so the route would have come from the fallback in every case anyway —
         an export that looks like a feature and is a dead letter. The hash
         first, because that is where the route lives in the Android build. */
      p_route: typeof location !== 'undefined' ? (location.hash || location.pathname) : '',
      /* The same stamp the sign-in record and the push registration use, put
         there by vite's define from package.json. It was reading a
         VITE_APP_VERSION that is not set anywhere, so every report would have
         said "dev" — a field that looks like it identifies a build and never
         does is worse than no field, because an operator would have trusted it
         while chasing a version-specific fault. */
      p_app_version: __APP_VERSION__,
      p_platform: platformName(),
    });
  } catch {
    /* Swallowed on purpose. See the note at the head of this file: the person
       is already looking at one failure and must not be given a second. */
  }
}
